import { useEffect, useState, useCallback, useMemo } from "react";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Lock, ShieldCheck, ArrowsCounterClockwise, FloppyDisk } from "@phosphor-icons/react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Roles whose permissions can be edited from the matrix. Admin column is
// implicitly granted everything (rendered read-only).
const EDITABLE_ROLES = [
  { key: "manager",   label: "Manager",   accent: "#FFD60A" },
  { key: "associate", label: "Associate", accent: "#64D2FF" }
];

export default function RolePermissionsTab() {
  const { fetchWithAuth } = useAuth();
  const [catalogue, setCatalogue] = useState([]);
  const [matrix, setMatrix] = useState([]);     // server snapshot
  const [draft, setDraft] = useState({});       // { [role]: { [permission]: bool } }
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const load = useCallback(async () => {
    const res = await fetchWithAuth(`${API_URL}/api/role-permissions`);
    if (!res.ok) {
      toast.error(res.data?.error || "Failed to load permissions");
      return;
    }
    setCatalogue(res.data.catalogue);
    setMatrix(res.data.matrix);
    // Seed the editable draft from the server snapshot.
    const next = { manager: {}, associate: {} };
    for (const row of res.data.matrix) {
      next.manager[row.key]   = row.manager;
      next.associate[row.key] = row.associate;
    }
    setDraft(next);
    setLoaded(true);
  }, [fetchWithAuth]);

  useEffect(() => { load(); }, [load]);

  const grouped = useMemo(() => {
    const buckets = {};
    for (const p of catalogue) {
      if (!buckets[p.group]) buckets[p.group] = [];
      buckets[p.group].push(p);
    }
    return buckets;
  }, [catalogue]);

  const dirtyByRole = useMemo(() => {
    const out = { manager: [], associate: [] };
    for (const row of matrix) {
      if (draft.manager?.[row.key]   !== row.manager)   out.manager.push(row.key);
      if (draft.associate?.[row.key] !== row.associate) out.associate.push(row.key);
    }
    return out;
  }, [draft, matrix]);

  const totalDirty = dirtyByRole.manager.length + dirtyByRole.associate.length;

  const toggle = (role, perm) => {
    // Admin-locked permissions cannot be granted to non-admin roles.
    if (perm.lockedToAdmin) return;
    setDraft(d => ({ ...d, [role]: { ...d[role], [perm.key]: !d[role]?.[perm.key] } }));
  };

  const save = async () => {
    if (totalDirty === 0) return;
    setSaving(true);
    try {
      for (const role of ["manager", "associate"]) {
        const updates = dirtyByRole[role].map(k => ({ permission: k, allowed: draft[role][k] }));
        if (updates.length === 0) continue;
        const res = await fetchWithAuth(`${API_URL}/api/role-permissions`, {
          method: "PUT",
          data: { role, updates }
        });
        if (!res.ok) {
          toast.error(`Save failed for ${role}: ${res.data?.error || "unknown error"}`);
          setSaving(false);
          return;
        }
      }
      toast.success(`Saved ${totalDirty} permission change${totalDirty !== 1 ? "s" : ""}. Affected users will see the new tabs on their next request.`);
      await load();
    } finally {
      setSaving(false);
    }
  };

  const resetToDefaults = async () => {
    if (!window.confirm("Reset Manager and Associate permissions to the canonical defaults? This cannot be undone (your audit log still has the changes).")) return;
    setSaving(true);
    try {
      const res = await fetchWithAuth(`${API_URL}/api/role-permissions/reset`, { method: "POST" });
      if (!res.ok) {
        toast.error(res.data?.error || "Reset failed");
      } else {
        toast.success("Permissions reset to defaults.");
        await load();
      }
    } finally {
      setSaving(false);
    }
  };

  if (!loaded) {
    return <div className="text-[#666] text-sm py-12 text-center" data-testid="role-perms-loading">Loading permissions…</div>;
  }

  return (
    <div className="space-y-6" data-testid="role-permissions-tab">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold tracking-tight">Role Permissions</h2>
          <p className="text-sm text-[#888] mt-1.5 max-w-2xl">
            Decide which tabs and actions <strong className="text-[#FFD60A]">Manager</strong> and <strong className="text-[#64D2FF]">Associate</strong> can see. Admin always has full access and cannot be locked out. Some permissions are flagged with a&nbsp;
            <Lock size={11} weight="bold" className="inline align-middle text-[#FF3B30]" /> — those are admin-only by design (security-sensitive).
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button
            onClick={resetToDefaults}
            disabled={saving}
            data-testid="reset-permissions-btn"
            className="px-3 py-1.5 rounded text-xs font-medium border border-[#2A2A2A] text-[#888] hover:border-[#444] hover:text-white transition-colors disabled:opacity-40">
            <ArrowsCounterClockwise size={12} weight="bold" className="inline mr-1 -mt-0.5" />
            Reset to defaults
          </button>
          <button
            onClick={save}
            disabled={saving || totalDirty === 0}
            data-testid="save-permissions-btn"
            className={`px-4 py-1.5 rounded text-xs font-bold transition-all ${
              totalDirty === 0
                ? "bg-[#1A1A1A] text-[#444] border border-[#222] cursor-not-allowed"
                : "bg-[#E85D24] text-white hover:bg-[#FF6A2C]"
            }`}>
            <FloppyDisk size={12} weight="bold" className="inline mr-1 -mt-0.5" />
            {totalDirty === 0 ? "No changes" : `Save ${totalDirty} change${totalDirty !== 1 ? "s" : ""}`}
          </button>
        </div>
      </div>

      {/* Matrix table */}
      <div className="border border-[#1A1A1A] rounded-lg overflow-hidden">
        {/* Column header */}
        <div className="grid grid-cols-[1fr_120px_120px_120px] gap-4 px-5 py-3 bg-[#0D0D0D] border-b border-[#1A1A1A] text-[10px] uppercase tracking-wider text-[#666] font-semibold">
          <div>Permission</div>
          <div className="text-center">
            <span className="text-[#FF3B30]">Admin</span>
            <ShieldCheck size={11} weight="fill" className="inline ml-1 -mt-0.5 text-[#FF3B30]" />
          </div>
          {EDITABLE_ROLES.map(r => (
            <div key={r.key} className="text-center" style={{ color: r.accent }}>{r.label}</div>
          ))}
        </div>

        {Object.entries(grouped).map(([groupName, perms]) => (
          <div key={groupName}>
            <div className="px-5 py-2 bg-[#080808] text-[10px] uppercase tracking-wider text-[#777] font-bold border-b border-[#151515]">
              {groupName}
            </div>
            {perms.map(perm => (
              <div
                key={perm.key}
                data-testid={`perm-row-${perm.key}`}
                className="grid grid-cols-[1fr_120px_120px_120px] gap-4 px-5 py-3 border-b border-[#121212] last:border-b-0 items-center hover:bg-[#0A0A0A]/60 transition-colors">
                <div className="min-w-0">
                  <div className="text-sm font-medium text-white flex items-center gap-2">
                    {perm.lockedToAdmin && (
                      <Lock size={11} weight="bold" className="text-[#FF3B30] shrink-0" title="Admin-only" />
                    )}
                    {perm.label}
                  </div>
                  <code className="text-[10px] text-[#555] font-mono">{perm.key}</code>
                </div>
                {/* Admin column — always granted, read-only */}
                <div className="flex justify-center">
                  <span className="inline-block w-5 h-5 rounded border bg-[#FF3B30]/15 border-[#FF3B30]/40 text-[#FF3B30] text-center text-[11px] leading-[19px] font-bold">✓</span>
                </div>
                {EDITABLE_ROLES.map(r => {
                  const checked = !!draft[r.key]?.[perm.key];
                  const disabled = perm.lockedToAdmin;
                  const dirty = dirtyByRole[r.key].includes(perm.key);
                  return (
                    <div key={r.key} className="flex justify-center">
                      <button
                        onClick={() => toggle(r.key, perm)}
                        disabled={disabled}
                        data-testid={`toggle-${r.key}-${perm.key}`}
                        className={`w-5 h-5 rounded border text-center text-[11px] leading-[19px] font-bold transition-all ${
                          disabled
                            ? "bg-[#1A1A1A] border-[#222] text-[#333] cursor-not-allowed"
                            : checked
                              ? "border-[2px]"
                              : "bg-transparent border-[#333] text-[#333] hover:border-[#555]"
                        } ${dirty ? "ring-2 ring-[#E85D24]/40" : ""}`}
                        style={checked && !disabled ? { background: `${r.accent}22`, borderColor: `${r.accent}66`, color: r.accent } : undefined}
                        title={disabled ? "Admin-only — cannot grant to this role" : checked ? "Granted" : "Denied"}>
                        {disabled ? "—" : checked ? "✓" : ""}
                      </button>
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        ))}
      </div>

      <p className="text-[11px] text-[#555] leading-relaxed">
        Changes go live the moment you press Save — affected users will see updated tabs on their next page refresh (no logout required).
      </p>
    </div>
  );
}
